import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { parseFrontmatter } from '../legal/frontmatter';
import { legalPath } from '../legal/legalConfig';

/**
 * Muestra la §5 «Facturación electrónica (CFDI)» de los TyC VERBATIM, leída de la
 * FUENTE ÚNICA servida como estático (docs/legal/v3.2/terminos-y-condiciones.md →
 * /legal/terminos-y-condiciones.md). No se copia el texto a mano: se extrae la
 * sección 5 del mismo documento publicado. Fallback a la liga si no carga.
 */

/** Extrae de "## 5." hasta el siguiente encabezado nivel-2 numerado ("## 6."). */
export function extractSection5(md: string): string | null {
  const lines = md.split(/\r?\n/);
  const startIdx = lines.findIndex((l) => /^##\s+5\.\s/.test(l));
  if (startIdx === -1) return null;
  let endIdx = lines.length;
  for (let i = startIdx + 1; i < lines.length; i++) {
    if (/^##\s+\d/.test(lines[i])) {
      endIdx = i;
      break;
    }
  }
  return lines.slice(startIdx, endIdx).join('\n').trim() || null;
}

type LoadState = 'loading' | 'ready' | 'error';

export function ContractSection5({ version }: { version: string }) {
  const href = legalPath('terminos-y-condiciones'); // /legal/terminos-y-condiciones
  const [state, setState] = useState<LoadState>('loading');
  const [section, setSection] = useState('');

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    fetch(`${href}.md`, { headers: { Accept: 'text/markdown, text/plain' } })
      .then(async (res) => {
        if (!res.ok) throw new Error('http');
        const text = await res.text();
        // Si el SPA devolvió index.html en vez del .md, trátalo como error.
        if (/^\s*<!doctype html/i.test(text) || text.includes('<div id="root">')) {
          throw new Error('spa-fallback');
        }
        const { content } = parseFrontmatter(text);
        const s5 = extractSection5(content);
        if (!s5) throw new Error('no-section');
        if (!cancelled) {
          setSection(s5);
          setState('ready');
        }
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [href]);

  return (
    <details className="mb-3 rounded-lg border border-slate-200 bg-slate-50">
      <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-slate-700">
        Leer la §5 «Facturación electrónica (CFDI)» (versión {version})
      </summary>
      <div className="max-h-72 overflow-y-auto border-t border-slate-200 px-3 py-2">
        {state === 'loading' && <p className="text-xs text-slate-500">Cargando el texto…</p>}
        {state === 'error' && (
          <p className="text-xs text-slate-600">
            No se pudo mostrar el texto aquí.{' '}
            <a href={href} target="_blank" rel="noopener noreferrer" className="underline">
              Abrir la §5 en los Términos y Condiciones
            </a>
            .
          </p>
        )}
        {state === 'ready' && (
          <div className="legal-prose text-xs">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{section}</ReactMarkdown>
          </div>
        )}
      </div>
    </details>
  );
}

export default ContractSection5;
